// Loop boundaries match othello_v2/sound-engine.ts and special-card-registry.ts.
// Loop loudness measured with EBU R128; keep phase 1's level as the reference.
// Remeasure changed tracks with scripts/verify_bgm_loudness.mjs.
export const BGM_TARGET_LUFS = -24.5;
export const PHASE_BGM = [
  { key: 'bgm-phase-1', loopStart: 0, loopEnd: 58.434783, loudnessLufs: -24.5 },
  { key: 'bgm-phase-2', loopStart: 0, loopEnd: undefined, loudnessLufs: -21.0 },
  { key: 'bgm-phase-3', loopStart: 0, loopEnd: 112 * 60 / 135, loudnessLufs: -14.3 },
  { key: 'bgm-phase-4', loopStart: 0, loopEnd: 40, loudnessLufs: -14.7 },
  { key: 'bgm-phase-5', loopStart: 9.6, loopEnd: 62.4, loudnessLufs: -15.5 },
] as const;

// PCM loop masters preserve sample-exact boundaries without codec padding.
export const HIDDEN_BGM = {
  'board-executor': { key: 'bgm-board-executor', path: 'assets/audio/bgm/board-executor-loop.wav',
    loopStart: 0, loopEnd: 2_457_600 / 48_000, loudnessLufs: -14.2 },
  'theory-incarnation': { key: 'bgm-theory-incarnation', path: 'assets/audio/bgm/theory-incarnation-loop.wav',
    loopStart: 0, loopEnd: 1_365_333 / 48_000, loudnessLufs: -14.2 },
} as const;

export type BgmCue = number | keyof typeof HIDDEN_BGM | 'silence';

function getTrack(cue: BgmCue) {
  return typeof cue === 'number' ? PHASE_BGM[cue - 1] : cue === 'silence' ? undefined : HIDDEN_BGM[cue];
}

export class PhaseBgm {
  private source: AudioBufferSourceNode | null = null;
  private readonly gain: GainNode;
  private phase: BgmCue = 0;
  private volume = 0;
  private offset = 0;
  private startedAt = 0;

  constructor(private readonly context: AudioContext, destination: AudioNode,
    private readonly getBuffer: (key: string) => AudioBuffer | undefined) {
    this.gain = context.createGain();
    this.gain.connect(destination);
  }

  setVolume(volume: number): void {
    this.volume = volume;
    this.applyVolume();
  }

  sync(phase: BgmCue, playing: boolean): void {
    if (phase !== this.phase) {
      this.stop();
      this.phase = phase;
      this.offset = 0;
      this.applyVolume();
    }
    if (!playing) { this.stop(); return; }
    if (this.source) return;
    const track = getTrack(phase);
    if (!track) return;
    const buffer = this.getBuffer(track.key);
    if (!buffer) return;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.loopStart = track.loopStart;
    source.loopEnd = Math.min(track.loopEnd ?? buffer.duration, buffer.duration);
    if (this.offset >= source.loopEnd) {
      this.offset = source.loopStart + (this.offset - source.loopStart) % (source.loopEnd - source.loopStart);
    }
    source.connect(this.gain);
    source.start(0, this.offset);
    this.startedAt = this.context.currentTime;
    this.source = source;
  }

  private stop(): void {
    if (!this.source) return;
    this.offset += this.context.currentTime - this.startedAt;
    this.source.stop();
    this.source.disconnect();
    this.source = null;
  }

  destroy(): void { this.stop(); this.gain.disconnect(); }

  private applyVolume(): void {
    const track = getTrack(this.phase);
    const phaseScale = track ? 10 ** ((BGM_TARGET_LUFS - track.loudnessLufs) / 20) : 1;
    this.gain.gain.value = this.volume * 0.5 * phaseScale;
  }
}
